<template>
    <div class="edverum-table-orm-paginator">
        <div class="paginator-divider paginator-divider--top" aria-hidden="true" />
        <div class="paginator-divider paginator-divider--bottom" aria-hidden="true" />

        <div class="paginator-left">
            <span class="paginator-summary">
                Showing: {{ firstRecord }} to {{ lastRecord }} of {{ formattedTotalRecords }} records
            </span>

            <e-dropdown
                :model-value="selectedPerPage"
                aria-role="list"
                class="per-page-dropdown"
                position="is-top-right"
            >
                <template #trigger="slotProps">
                    <e-button
                        :label="`${selectedPerPage} per page`"
                        type="is-default is-outline"
                        class="per-page-trigger"
                        :icon-right="slotProps?.active ? 'angle-up' : 'angle-down'"
                    />
                </template>

                <e-dropdown-item
                    v-for="size in resolvedPerPageOptions"
                    :key="size"
                    aria-role="listitem"
                    :model-value="size"
                    @click="selectPerPage(size)"
                >
                    {{ size }} per page
                </e-dropdown-item>
            </e-dropdown>

            <span v-if="disclaimerText" class="disclaimer-text">
                <e-icon icon="exclamation-triangle" class="disclaimer-icon" />
                {{ disclaimerText }}
            </span>
        </div>

        <div class="paginator-right">
            <span class="paginator-muted">Go to page:</span>
            <input
                v-model.number="selectedPage"
                type="number"
                class="page-input"
                :min="1"
                :max="totalPages"
                @change="handlePageSelect"
                @keyup.enter="handlePageSelect"
            />

            <div class="page-buttons">
                <template v-for="(page, index) in pageButtons" :key="index">
                    <span v-if="page === '...'" class="page-ellipsis">...</span>
                    <button
                        v-else
                        type="button"
                        class="page-btn"
                        :class="{ active: page === currentPage }"
                        @click="$emit('page-change', page)"
                    >
                        {{ page }}
                    </button>
                </template>
            </div>

            <button
                type="button"
                class="paginator-btn"
                :disabled="currentPage === 1 || totalRecords === 0"
                aria-label="Previous page"
                @click="$emit('page-change', currentPage - 1)"
            >
                <e-icon icon="angle-left" />
            </button>

            <button
                type="button"
                class="paginator-btn"
                :disabled="currentPage === totalPages || totalRecords === 0"
                aria-label="Next page"
                @click="$emit('page-change', currentPage + 1)"
            >
                <e-icon icon="angle-right" />
            </button>
        </div>
    </div>
</template>

<script>
import { DEFAULT_TABLE_PER_PAGES } from '@/constants/TablePagination.js';

export default {
    name: 'EdverumTableOrmPaginator',
    props: {
        currentPage: {
            type: Number,
            required: true,
        },
        totalPages: {
            type: Number,
            required: true,
        },
        perPage: {
            type: Number,
            default: 25,
        },
        perPageOptions: {
            type: Array,
            default: () => [...DEFAULT_TABLE_PER_PAGES],
        },
        totalRecords: {
            type: Number,
            required: true,
        },
        disclaimerText: {
            type: String,
            default: '',
        },
    },
    emits: ['page-change', 'per-page-change'],
    data() {
        return {
            selectedPerPage: this.perPage,
            selectedPage: this.currentPage,
        };
    },
    computed: {
        resolvedPerPageOptions() {
            const options = (this.perPageOptions || []).map(Number).filter(n => Number.isFinite(n) && n > 0);

            return options.length ? options : [...DEFAULT_TABLE_PER_PAGES];
        },
        formattedTotalRecords() {
            return Number(this.totalRecords || 0).toLocaleString();
        },
        firstRecord() {
            if (this.totalRecords === 0) return 0;
            return (this.currentPage - 1) * this.perPage + 1;
        },
        lastRecord() {
            const last = this.currentPage * this.perPage;
            return Math.min(last, this.totalRecords);
        },
        pageButtons() {
            const pages = [];
            const current = this.currentPage;
            const total = this.totalPages;

            if (total <= 1) return pages;

            // Add page 1 and ellipsis if we're far from the beginning
            const firstInRange = current === 1 ? 1 : current - 1;
            if (firstInRange > 2) {
                pages.push(1);
                pages.push('...');
            }

            // If on page 1, show pages 1, 2, (3 if exists)
            if (current === 1) {
                pages.push(1);
                if (total >= 2) pages.push(2);
                if (total >= 3) pages.push(3);
            } else {
                // Show previous, current, and next page
                if (current > 1) pages.push(current - 1);
                pages.push(current);
                if (current < total) pages.push(current + 1);
            }

            // Add ellipsis and last page if needed
            const lastInPages = pages[pages.length - 1];
            if (lastInPages < total - 1) {
                pages.push('...');
            }
            if (lastInPages < total) {
                pages.push(total);
            }

            return pages;
        },
    },
    watch: {
        currentPage(newVal) {
            this.selectedPage = newVal;
        },
        perPage(newVal) {
            this.selectedPerPage = newVal;
        },
    },
    methods: {
        selectPerPage(size) {
            if (size === this.perPage) {
                return;
            }

            this.selectedPerPage = size;
            this.$emit('per-page-change', size);
        },
        handlePageSelect() {
            const validPage = Math.max(1, Math.min(this.selectedPage || 1, this.totalPages));
            this.selectedPage = validPage;
            this.$emit('page-change', validPage);
        },
    },
};
</script>

<style lang="scss" scoped>
/* Unified footer band: theme tokens follow light + app-dark. */
.edverum-table-orm-paginator {
    --paginator-vertical-padding: 20px;
    --paginator-horizontal-padding: 24px;
    --paginator-surface: var(--p-content-background);
    --paginator-surface-hover: var(--surface-hover);
    --paginator-border: var(--p-content-border-color);
    --paginator-text: var(--p-text-color);
    --paginator-text-muted: var(--p-text-muted-color);
    --paginator-accent: var(--p-primary-color);
    --paginator-accent-contrast: var(--p-primary-contrast-color);
    --paginator-focus: var(--p-focus-ring-color);

    position: relative;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 1.25rem;
    box-sizing: border-box;
    width: 100%;
    /* Equal top/bottom inset so controls sit centered in the footer band. */
    padding: var(--paginator-vertical-padding) var(--paginator-horizontal-padding);
    margin: 0;
    background: var(--paginator-surface);
    color: var(--paginator-text);
}

.paginator-divider {
    position: absolute;
    right: -16px;
    left: -16px;
    border-top: 1px solid var(--paginator-border);
    pointer-events: none;

    &--top {
        top: -10px;
    }

    &--bottom {
        bottom: -10px;
    }
}

.paginator-left {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    min-width: 0;
    flex-wrap: wrap;
    margin: 0;
}

.paginator-right {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 0 0 0 auto;
    flex-wrap: wrap;
}

.paginator-summary {
    color: var(--paginator-text);
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
    white-space: nowrap;
}

.paginator-muted {
    color: var(--paginator-text-muted);
    font-size: 15px;
    font-weight: 400;
    line-height: 1.4;
    white-space: nowrap;
}

.per-page-dropdown {
    display: inline-flex;
    align-items: center;
    margin: 0;
    line-height: 1;
}

.per-page-dropdown :deep(.dropdown-trigger),
.per-page-dropdown :deep(.dropdown-menu) {
    margin: 0;
}

.per-page-dropdown :deep(.per-page-trigger),
.per-page-dropdown :deep(.button.per-page-trigger) {
    height: 2.75rem;
    min-height: 2.75rem;
    margin: 0;
    padding: 0 1rem;
    border: 1px solid var(--paginator-border) !important;
    border-radius: 0.5rem;
    background-color: var(--paginator-surface) !important;
    color: var(--paginator-text-muted) !important;
    font-size: 15px;
    font-weight: 400;
    line-height: 1;
    box-shadow: none !important;
    vertical-align: middle;

    .icon {
        margin-left: 0.5rem;
        color: var(--paginator-text-muted) !important;
    }

    &:hover {
        border-color: var(--paginator-border) !important;
        color: var(--paginator-text-muted) !important;
        background-color: var(--paginator-surface-hover) !important;
    }

    &:focus {
        outline: none;
        border-color: var(--paginator-focus) !important;
        color: var(--paginator-text-muted) !important;
        background-color: var(--paginator-surface) !important;
    }
}

.disclaimer-text {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--paginator-text-muted);
    font-size: 15px;
    font-weight: 400;
    white-space: nowrap;
}

.disclaimer-icon {
    color: #f59e0b;
}

.page-input {
    height: 2.75rem;
    width: 5.5rem;
    margin: 0;
    padding: 0 0.875rem;
    border: 1px solid var(--paginator-border);
    border-radius: 0.5rem;
    background-color: var(--paginator-surface);
    color: var(--paginator-text-muted);
    font-size: 15px;
    font-weight: 400;
    line-height: 1;
    text-align: center;

    &:focus {
        outline: none;
        border-color: var(--paginator-focus);
        color: var(--paginator-text);
    }

    /* Remove spinner arrows in Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Remove spinner arrows in Firefox */
    &[type='number'] {
        -moz-appearance: textfield;
    }
}

.page-buttons {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.page-btn {
    height: 2.75rem;
    min-width: 2.75rem;
    margin: 0;
    padding: 0 0.875rem;
    border: 1px solid var(--paginator-border);
    border-radius: 0.5rem;
    background-color: var(--paginator-surface);
    color: var(--paginator-text-muted);
    font-size: 15px;
    font-weight: 400;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: var(--paginator-surface-hover);
        color: var(--paginator-text);
    }

    &.active {
        background-color: var(--paginator-accent);
        color: var(--paginator-accent-contrast);
        border-color: var(--paginator-accent);
        font-weight: 500;
    }
}

.page-ellipsis {
    padding: 0 0.35rem;
    color: var(--paginator-text-muted);
    font-size: 15px;
    font-weight: 400;
}

.paginator-btn {
    height: 2.75rem;
    width: 2.75rem;
    margin: 0;
    border: 1px solid var(--paginator-border);
    border-radius: 0.5rem;
    background-color: var(--paginator-surface);
    color: var(--paginator-text-muted);
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    line-height: 1;

    &:hover:not(:disabled) {
        background-color: var(--paginator-surface-hover);
        color: var(--paginator-text);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
    }
}

@media (max-width: 640px) {
    .paginator-right {
        margin-left: 0;
    }

    .paginator-summary,
    .paginator-muted {
        white-space: normal;
    }
}
</style>
